import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, X } from "lucide-react";
import "./BronchoscopySection.css";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";
import PulmonologyMotifChips from "../PulmonologyMotifChips/PulmonologyMotifChips";

const findings = ["normalAirways", "inflammation", "lesion", "secretions", "bal", "biopsy"];

export default function BronchoscopySection({ patientId, record, saveRecord, saving, canSave }) {
  const { t } = useTranslation("pulmonology");
  const [selected, setSelected] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [indication, setIndication] = useState("");
  const [sampleId, setSampleId] = useState("");
  const [pathology, setPathology] = useState("");
  const [procedureDate, setProcedureDate] = useState("");
  const persistedRecord = Array.isArray(record) ? record[0] : record;

  useEffect(() => {
    if (!persistedRecord) return;
    setSelected(persistedRecord.findings || []);
    setIndication(persistedRecord.indication || "");
    setSampleId(persistedRecord.labSampleId || "");
    setPathology(persistedRecord.pathologyLink || "");
    setProcedureDate(persistedRecord.procedureDate ? String(persistedRecord.procedureDate).slice(0, 10) : "");
  }, [persistedRecord]);

  const saveBronchoscopy = () => saveRecord?.("bronchoscopy", {
    procedure: "Bronchoscopy",
    status: selected.length || indication.trim() ? "Requested" : "Not requested",
    indication,
    labSampleId: sampleId || null,
    pathologyLink: pathology || null,
    procedureDate: procedureDate || null,
    findings: selected,
  }, persistedRecord?.id);

  return (
    <div className="pulmo-form">
      <div className="pulmo-procedure"><div><strong>{t("bronchoscopy.title")}</strong><small>{persistedRecord?.status || t("common.notRecorded")}</small></div><span className="pulmo-tag is-info">{persistedRecord ? t("common.requested") : t("common.notRequested")}</span></div>
      <div className="pulmo-two-col"><label className="pulmo-input-field"><span>{t("bronchoscopy.indication")}</span><input value={indication} onChange={(event) => setIndication(event.target.value)} /></label><label className="pulmo-input-field"><span>{t("bronchoscopy.sampleId")}</span><input value={sampleId} onChange={(event) => setSampleId(event.target.value)} /></label><label className="pulmo-input-field"><span>{t("bronchoscopy.pathology")}</span><input value={pathology} onChange={(event) => setPathology(event.target.value)} /></label><label className="pulmo-input-field"><span>{t("bronchoscopy.procedureDate")}</span><input type="date" value={procedureDate} onChange={(event) => setProcedureDate(event.target.value)} /></label></div>
      <PulmonologyMotifChips
        key={`${patientId}-bronchoscopy`}
        patientId={patientId}
        group="bronchoscopy"
        defaultOptions={findings}
        selected={selected}
        labelForOption={(option) => findings.includes(option) ? t(`bronchoscopy.findings.${option}`) : option}
        onToggle={(option) => setSelected((items) => items.includes(option) ? items.filter((item) => item !== option) : [...items, option])}
        onRemove={(option) => setSelected((items) => items.filter((item) => item !== option))}
      />
      <label className="pulmo-add-row pulmo-procedure-attach">
        <Paperclip size={12} /> {t("bronchoscopy.attachReport")}
        <input type="file" accept=".pdf,image/*" multiple aria-label={t("bronchoscopy.attachReport")} onChange={(event) => {
          const files = Array.from(event.target.files || []);
          setAttachments((current) => [...current, ...files]);
          event.target.value = "";
        }} />
      </label>
      {attachments.length > 0 && <div className="pulmo-procedure-files" aria-live="polite">
        {attachments.map((file, index) => <div className="pulmo-procedure-file" key={`${file.name}-${index}`}>
          <span><Paperclip size={11} /> {file.name}</span>
          <button type="button" aria-label={t("bronchoscopy.removeAttachment", { name: file.name })} onClick={() => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}><X size={12} /></button>
        </div>)}
      </div>}
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveBronchoscopy} saving={saving} disabled={!canSave} />
      </div>
    </div>
  );
}
